<template>
  <v-dialog max-width="700px" v-model="showDialog" @update:model-value="handleDialogClose" @keydown.esc="handleEscape">
    <v-card>
      <v-card-title class="bg-primary text-white">
        <h4>{{ isSimServiceAddition ? 'Adauga Serviciu pe SIM' : (isRenewMode ? 'Renew Service' : 'Add New Client Service') }}</h4>
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- SIM Info Banner (when adding service to SIM) -->
        <v-alert v-if="isSimServiceAddition && renewData" type="info" density="compact" class="mb-4">
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-sim</v-icon>
            <div>
              <strong>SIM:</strong> {{ renewData.imsiNumber }}
              <span v-if="renewData.msisdnNumber" class="ml-3">
                <strong>MSISDN:</strong> {{ renewData.msisdnNumber }}
              </span>
              <span class="ml-3">
                <strong>Client:</strong> {{ renewData.clientName }}
              </span>
            </div>
          </div>
        </v-alert>

        <!-- Step 1: Select Client (hidden when adding to SIM) -->
        <v-row v-if="!isSimServiceAddition">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-bold mb-2">1. Select Client</div>
            <v-autocomplete
              v-model="selectedClientId"
              v-model:search="clientSearchText"
              :items="clientsList"
              item-value="id"
              item-title="displayName"
              label="Select Client"
              outlined
              dense
              clearable
              auto-select-first
              :loading="loadingClients"
              :error-messages="clientSelectionError"
              @update:model-value="onClientChange"
              @focus="onClientFieldFocus"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item
                  v-bind="props"
                  :subtitle="item.raw.isOwn ? 'Own Client' : 'Subclient'"
                  :disabled="!item.raw.isActive"
                  :class="{ 'text-grey': !item.raw.isActive }"
                  @click.stop="!item.raw.isActive && showInactiveClientError(item.raw)"
                ></v-list-item>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-divider v-if="!isSimServiceAddition" class="my-3"></v-divider>

        <!-- Step 2: Select Service Type (shown for both modes, numbered differently) -->
        <v-row v-if="!isSimServiceAddition || (isSimServiceAddition && simServiceTypes.length > 1)">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-bold mb-2">
              {{ isSimServiceAddition ? '1. Tip Serviciu' : '2. Service Type' }}
            </div>
            <v-radio-group v-model="selectedServiceType" row dense @update:model-value="onServiceTypeChange">
              <v-radio
                v-if="isSimServiceAddition"
                value="all"
                label="Toate"
              ></v-radio>
              <v-radio
                v-for="st in (isSimServiceAddition ? simServiceTypes : serviceTypes)"
                :key="st.id"
                :value="st.name"
                :label="formatServiceTypeName(st.name)"
              ></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <v-divider v-if="!isSimServiceAddition" class="my-3"></v-divider>

        <!-- SIM Selector (for normal mode with imsi_subscription/valability types) -->
        <v-row v-if="requiresSimSelection && selectedClientId">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-bold mb-2">
              2.5. Selecteaza SIM-ul
            </div>
            <v-autocomplete
              v-model="selectedSimId"
              :items="clientSims"
              item-value="id"
              item-title="label"
              label="Selecteaza SIM"
              outlined
              dense
              clearable
              :loading="loadingClientSims"
              @update:model-value="onSimSelected"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item
                  v-bind="props"
                  :disabled="isSimDisabledForType(item.raw)"
                >
                  <template v-slot:subtitle>
                    <span v-if="item.raw.client_service_status" class="mr-2">
                      Status: {{ item.raw.client_service_status }}
                    </span>
                    <span v-if="item.raw.service_name" class="mr-2">
                      | {{ item.raw.service_name }}
                    </span>
                    <v-chip
                      v-if="item.raw.has_active_subscription"
                      size="x-small"
                      color="warning"
                      class="ml-2"
                    >
                      subscriptie activa
                    </v-chip>
                    <v-chip
                      v-if="selectedServiceType === 'valability' && !item.raw.has_active_subscription"
                      size="x-small"
                      color="error"
                      class="ml-2"
                    >
                      fara subscriptie
                    </v-chip>
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
            <v-alert v-if="clientSims.length === 0 && !loadingClientSims" type="warning" density="compact" class="mt-2">
              Clientul selectat nu are SIM-uri disponibile.
            </v-alert>
          </v-col>
        </v-row>

        <!-- Selected SIM Info -->
        <v-row v-if="requiresSimSelection && selectedSimData">
          <v-col cols="12">
            <v-alert type="info" density="compact" class="mt-1">
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-sim</v-icon>
                <div>
                  <strong>SIM selectat:</strong> Serial: {{ selectedSimData.serial || '-' }}
                  <span class="ml-3"><strong>IMSI:</strong> {{ selectedSimData.imsi || '-' }}</span>
                  <span v-if="selectedSimData.msisdn" class="ml-3"><strong>MSISDN:</strong> {{ selectedSimData.msisdn }}</span>
                </div>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <v-divider v-if="requiresSimSelection" class="my-3"></v-divider>

        <!-- Step 3: Select Service -->
        <v-row v-if="selectedServiceType || isSimServiceAddition">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ isSimServiceAddition ? '2. Selecteaza Serviciu' : '3. Select Service' }}</div>
            <div class="text-caption mb-2 text-grey">
              ({{ filteredServices.length }} servicii disponibile{{ !isSimServiceAddition ? ` din ${services.length} total` : '' }})
            </div>
            <v-autocomplete
              v-if="filteredServices.length > 0"
              v-model="selectedServiceId"
              v-model:search="serviceSearchText"
              :items="filteredServices"
              item-value="id"
              item-title="name"
              :label="isSimServiceAddition ? 'Selecteaza Serviciu' : 'Select Service'"
              outlined
              dense
              clearable
              auto-select-first
              :loading="loadingServices"
              @update:model-value="onServiceChange"
              @focus="serviceSearchText = ''"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item
                  v-bind="props"
                  :disabled="item.raw.canAdd === false"
                  :class="{ 'text-grey': item.raw.canAdd === false }"
                >
                  <template v-slot:subtitle>
                    <span v-if="item.raw.price">{{ item.raw.price }} {{ item.raw.currency || item.raw.currency_symbol || item.raw.currency_name }}</span>
                    <span v-else class="text-grey">Price not set</span>
                    <span v-if="item.raw.validity" class="ml-2 text-caption">({{ item.raw.validity }} zile)</span>
                    <span v-else-if="item.raw.billing_type_name" class="ml-2 text-caption">({{ item.raw.billing_type_name }})</span>
                    <!-- Show blocked/existing service indicator for SIM mode -->
                    <span v-if="item.raw.blockedBySubscription" class="ml-2">
                      <v-chip size="x-small" color="error">subscriptie activa pe SIM</v-chip>
                    </span>
                    <span v-else-if="item.raw.existsOnSim" class="ml-2">
                      <v-chip v-if="item.raw.canAdd" size="x-small" color="info">permite multiple</v-chip>
                      <v-chip v-else size="x-small" color="error">nu permite multiple</v-chip>
                    </span>
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
            <v-alert v-else type="warning" density="compact">
              Nu exista servicii disponibile{{ !isSimServiceAddition ? ` pentru tipul "${formatServiceTypeName(selectedServiceType)}"` : '' }}
            </v-alert>
          </v-col>
        </v-row>

        <!-- Warning for blocked subscription or service that doesn't allow multiple (SIM mode) -->
        <v-row v-if="isSimServiceAddition && selectedService && !selectedService.canAdd">
          <v-col cols="12">
            <v-alert type="error" density="compact">
              <template v-if="selectedService.blockedBySubscription">
                <strong>SIM-ul are deja o subscriptie activa. Termina subscriptia existenta inainte de a adauga una noua.</strong>
              </template>
              <template v-else>
                <strong>Acest serviciu exista deja pe SIM si nu permite adaugari multiple.</strong>
                <div class="text-caption">Status actual: {{ selectedService.existingServiceStatus }}</div>
              </template>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Price Display for SIM service addition or client/imsi types -->
        <v-row v-if="selectedService && (isSimServiceAddition || !requiresMsisdnSelection)">
          <v-col cols="12">
            <v-alert type="info" density="compact" class="mt-2">
              <div class="d-flex justify-space-between align-center">
                <span><strong>Service Price:</strong></span>
                <span v-if="selectedService.price" class="text-h6">
                  {{ selectedService.price }} {{ selectedService.currency_symbol || selectedService.currency_name }}
                </span>
                <span v-else class="text-warning">Price not configured</span>
              </div>
              <div class="text-caption mt-1">
                Billing: {{ selectedService.billing_type_name }}
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- IMSI/Serial List Input (for imsi service type) -->
        <v-row v-if="selectedServiceType === 'imsi' && selectedServiceId">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-bold mb-2">IMSI / Serial List</div>
            <v-textarea
              v-model="imsiSerialListText"
              label="Enter IMSI or Serial numbers (one per line)"
              hint="Each line should contain only digits (0-9). Example: 123456789012345"
              persistent-hint
              outlined
              rows="5"
              :error-messages="imsiSerialListErrors"
              @input="validateImsiSerialList"
            ></v-textarea>
            <div v-if="parsedImsiSerialList.length > 0" class="text-caption mt-1 text-success">
              {{ parsedImsiSerialList.length }} valid entries
            </div>
          </v-col>
        </v-row>

        <!-- IMSI Charging Options (for imsi service type) -->
        <v-row v-if="selectedServiceType === 'imsi' && selectedServiceId">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-bold mb-2">IMSI Charging Options</div>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="doNotChargeImsisCost"
              label="Nu taxa costul IMSI-urilor"
              color="error"
              hide-details
              density="compact"
              @update:model-value="onDoNotChargeImsisCostChange"
            ></v-switch>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="chargeStandardPrice"
              label="Charge standard price"
              color="success"
              hide-details
              density="compact"
              :disabled="doNotChargeImsisCost"
              @update:model-value="onChargeStandardPriceChange"
            ></v-switch>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="chargeCostOfSims"
              label="Charge custom cost"
              color="primary"
              hide-details
              density="compact"
              :disabled="doNotChargeImsisCost"
              @update:model-value="onChargeCostOfSimsChange"
            ></v-switch>
          </v-col>
        </v-row>

        <v-row v-if="selectedServiceType === 'imsi' && selectedServiceId && chargeCostOfSims && !doNotChargeImsisCost">
          <v-col cols="6">
            <v-text-field
              v-model.number="pricePerSim"
              label="Price per sim"
              type="number"
              min="0"
              step="0.01"
              outlined
              dense
              :prefix="selectedService?.currency_symbol || selectedService?.currency_name || ''"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="fixedPrice"
              label="Price per transaction"
              type="number"
              min="0"
              step="0.01"
              outlined
              dense
              :prefix="selectedService?.currency_symbol || selectedService?.currency_name || ''"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Set Delivery Costs (for imsi service type) -->
        <v-row v-if="selectedServiceType === 'imsi' && selectedServiceId">
          <v-col cols="12">
            <v-switch
              v-model="setDeliveryCosts"
              label="Set Delivery Costs"
              color="primary"
              hide-details
              density="compact"
            ></v-switch>
          </v-col>
        </v-row>

        <v-row v-if="selectedServiceType === 'imsi' && selectedServiceId && setDeliveryCosts">
          <v-col cols="12">
            <v-radio-group v-model="selectedDeliveryTypeId" density="compact">
              <v-radio
                v-for="dt in deliveryTypes"
                :key="dt.id"
                :value="dt.id"
                :label="`${dt.name} (${dt.cost} ${selectedService?.currency_symbol || selectedService?.currency_name || ''})`"
              ></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <!-- Special Delivery Cost (for imsi service type) -->
        <v-row v-if="selectedServiceType === 'imsi' && selectedServiceId && setDeliveryCosts">
          <v-col cols="12" md="6">
            <v-switch
              v-model="setSpecialDeliveryCost"
              label="Set special delivery cost"
              color="primary"
              hide-details
              density="compact"
            ></v-switch>
          </v-col>
          <v-col cols="12" md="6" v-if="setSpecialDeliveryCost">
            <v-text-field
              v-model.number="specialDeliveryCost"
              label="Special delivery cost"
              type="number"
              min="0"
              step="1"
              outlined
              dense
              :prefix="selectedService?.currency_symbol || selectedService?.currency_name || ''"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- IMSI Type Selection (for imsi_msisdn service type) -->
        <v-row v-if="selectedServiceType === 'imsi_msisdn' && selectedServiceId && imsiTypes.length > 0">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-bold mb-2">IMSI Type</div>
            <v-select
              v-model="selectedImsiTypeId"
              :items="imsiTypes"
              item-title="name"
              item-value="id"
              label="Select IMSI Type"
              outlined
              dense
              clearable
            ></v-select>
          </v-col>
        </v-row>

        <!-- MSISDN Selection Required Notice -->
        <v-row v-if="selectedService && requiresMsisdnSelection && !selectedMsisdnData">
          <v-col cols="12">
            <v-alert type="warning" density="compact" class="mt-2">
              <div class="d-flex justify-space-between align-center">
                <span>This service requires selecting a phone number (MSISDN)</span>
                <v-btn color="primary" size="small" @click="openMsisdnSelector">
                  Select MSISDN
                </v-btn>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Selected MSISDN Display -->
        <v-row v-if="selectedMsisdnData">
          <v-col cols="12">
            <v-alert type="success" density="compact" class="mt-2">
              <div class="d-flex justify-space-between align-center">
                <div>
                  <strong>Selected MSISDN:</strong> {{ selectedMsisdnData.msisdn }}
                  <div class="text-caption">
                    {{ selectedMsisdnData.billing_type === 'buy' ? 'Buy' : 'Rent' }}
                    <span v-if="selectedMsisdnData.billing_period_name"> - {{ selectedMsisdnData.billing_period_name }}</span>
                  </div>
                </div>
                <div>
                  <span class="text-h6 mr-2">{{ selectedMsisdnData.price }} {{ selectedMsisdnData.currency }}</span>
                  <v-btn color="secondary" size="x-small" @click="openMsisdnSelector">Change</v-btn>
                </div>
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <!-- Step 4: Additional Options -->
        <v-row v-if="selectedServiceId">
          <v-col cols="12">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ isSimServiceAddition ? '3.' : '4.' }} Additional Options</div>
          </v-col>
        </v-row>

        <v-row v-if="selectedServiceId">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="description"
              :label="isSimServiceAddition ? 'Descriere (optional)' : 'Description (optional)'"
              outlined
              dense
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="autorenew"
              label="Autorenew"
              color="success"
              hide-details
              density="compact"
            ></v-switch>
          </v-col>
        </v-row>

        <v-row v-if="selectedServiceId">
          <v-col :cols="serviceHasValidity ? 12 : 6">
            <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition">
              <template #activator="{ props }">
                <v-text-field
                  v-model="formattedStartDate"
                  label="Start Date"
                  readonly
                  v-bind="props"
                  dense
                  outlined
                  prepend-inner-icon="mdi-calendar"
                ></v-text-field>
              </template>
              <v-date-picker v-model="startDate" @update:model-value="updateFormattedStartDate"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col v-if="!serviceHasValidity" cols="6">
            <v-text-field
              v-model="expirationDate"
              label="Expiration Date"
              readonly
              dense
              outlined
              prepend-inner-icon="mdi-calendar-end"
            ></v-text-field>
          </v-col>
        </v-row>
        <!-- Show validity info when service has validity -->
        <v-row v-if="selectedServiceId && serviceHasValidity">
          <v-col cols="12">
            <v-alert type="info" density="compact" variant="tonal">
              <v-icon start size="small">mdi-calendar-clock</v-icon>
              Valabilitate: {{ selectedService.validity }} zile (expira: {{ expirationDate }})
            </v-alert>
          </v-col>
        </v-row>

        <!-- Billing Type Info -->
        <v-alert
          v-if="targetClient && selectedServiceId"
          :type="billingAlertType"
          density="compact"
          class="mt-4"
        >
          <div v-if="selectedServiceBillingType === 'free'">
            <strong>Free Service:</strong> No payment required.
          </div>
          <div v-else-if="isTargetClientPrepaid">
            <strong>Client Prepaid:</strong> {{ targetClient.name }} - Payment will be required after adding the service.
          </div>
          <div v-else-if="selectedServiceBillingType === 'onetime'">
            <strong>One-time Service:</strong> {{ targetClient.name }} - A one-time charge will appear in the billing report.
          </div>
          <div v-else>
            <strong>Client Postpaid:</strong> {{ targetClient.name }} - Service will appear in the next billing report.
          </div>
        </v-alert>

        <!-- Action Buttons -->
        <v-row justify="space-around" class="mt-4">
          <v-btn color="grey" @click="closeDialog">Cancel</v-btn>
          <v-btn
            :color="submitButtonColor"
            @click="submitNewClientService"
            :loading="submitting"
            :disabled="!canSubmit"
          >
            {{ submitButtonText }}
          </v-btn>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- MSISDN Selector Dialog -->
  <SelectAvailableMsisdn
    v-if="showMsisdnSelector"
    :currentSimIndex="0"
    @msisdnSelected="onMsisdnSelected"
    @dialogSelectAvailableMsisdnEscapedEvent="closeMsisdnSelector"
  />

  <!-- IMSI Allocation Confirmation Dialog -->
  <v-dialog v-model="showImsiAllocationConfirm" max-width="600px" persistent>
    <v-card>
      <v-card-title class="bg-warning text-white">
        <v-icon class="mr-2">mdi-alert</v-icon>
        Confirmare necesară
      </v-card-title>

      <v-card-text class="pa-4">
        <div class="text-body-1 mb-4">
          {{ imsiAllocationMessage }}
        </div>

        <v-alert type="warning" density="compact" class="mb-4">
          <strong>Următoarele IMSI-uri sunt deja alocate altor clienți:</strong>
        </v-alert>

        <v-table density="compact" class="mb-4">
          <thead>
            <tr>
              <th>IMSI Serial</th>
              <th>Client</th>
              <th>Serviciu</th>
              <th>Tip</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="alloc in otherClientAllocations" :key="alloc.serial">
              <td><code>{{ alloc.serial }}</code></td>
              <td>{{ alloc.client_name }}</td>
              <td>{{ alloc.service_name }}</td>
              <td>
                <v-chip size="x-small" color="info">
                  {{ alloc.service_type || 'imsi' }}
                </v-chip>
              </td>
              <td>
                <v-chip size="x-small" :color="alloc.status === 'active' ? 'success' : 'warning'">
                  {{ alloc.status }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-table>

        <v-alert type="info" density="compact">
          Dacă confirmați, serviciile existente pentru aceste IMSI-uri vor fi <strong>terminate</strong> automat și vor fi alocate noului client.
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="grey" variant="outlined" @click="cancelImsiAllocationConfirm">
          Anulează
        </v-btn>
        <v-btn color="warning" variant="flat" @click="confirmTerminateAndAdd" :loading="submitting">
          Confirmă și Termină Serviciile
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Payment Modal (BT - Banca Transilvania) -->
  <v-dialog v-model="showPaymentModal" max-width="600px" persistent>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center bg-primary text-white">
        <span class="text-h6">Comanda a fost recepționată</span>
        <v-btn icon variant="text" color="white" @click="closePaymentModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="text-center pa-6">
        <div class="text-h6 text-primary mb-4">
          Vei fi direcționat către Banca Transilvania pentru plata online
        </div>

        <div class="text-body-1 mb-4">
          {{ paymentModalMessage }}
        </div>

        <a :href="paymentLink" target="_blank" class="text-decoration-none" @click="onPaymentLinkClick">
          <div class="my-6">
            <img
              src="@/assets/images/BTlogoLung.png"
              alt="Banca Transilvania"
              style="max-width: 250px;"
            />
          </div>

          <div class="text-h6 text-primary mb-4">
            {{ paymentOrderDetails }}
          </div>

          <v-btn color="warning" size="large" class="mt-4">
            Plătește online
          </v-btn>
        </a>

        <div class="text-h6 text-primary mt-6">
          Vei primi un email de confirmare după efectuarea plății!
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getServicesForAddClientService } from "@/composables/useGetServicesForAddClientService";
import { addNewClientService } from "@/composables/useAddNewClientService";
import { getSimsList } from "@/composables/useGetSimsAPI";
import SelectAvailableMsisdn from "@/components/SelectAvailableMsisdn.vue";
import store from "@/store";

export default defineComponent({
  name: 'AddNewClientService',
  components: {
    SelectAvailableMsisdn
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    renewData: {
      type: Object,
      default: null
    },
    initialClientId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['close', 'serviceAdded'],
  data() {
    return {
      showDialog: false,
      token: '',
      loadingClients: false,
      loadingServices: false,
      submitting: false,

      // Client selection
      currentClient: null,
      targetClient: null,
      subclients: [],
      selectedClientId: null,
      clientSearchText: '',
      clientSelectionError: '',

      // Service type selection
      serviceTypes: [],
      selectedServiceType: null,

      // Service selection
      services: [],
      selectedServiceId: null,
      serviceSearchText: '',

      // MSISDN selection (for msisdn/imsi_msisdn types)
      showMsisdnSelector: false,
      selectedMsisdnData: null,

      // Additional options
      description: '',
      autorenew: false,
      startDate: null,
      formattedStartDate: '',
      expirationDate: '',
      startDateMenu: false,

      // IMSI/Serial list (for imsi service type)
      imsiSerialListText: '',
      imsiSerialListErrors: [],

      // Charge cost of sims (for imsi service type)
      doNotChargeImsisCost: false,
      chargeStandardPrice: true,
      chargeCostOfSims: false,
      pricePerSim: 0,
      fixedPrice: 0,

      // Delivery costs (for imsi service type)
      deliveryTypes: [],
      setDeliveryCosts: false,
      selectedDeliveryTypeId: null,
      setSpecialDeliveryCost: false,
      specialDeliveryCost: 0,

      // IMSI types (for imsi_msisdn service type)
      imsiTypes: [],
      selectedImsiTypeId: null,

      // Payment modal
      showPaymentModal: false,
      paymentLink: '',
      paymentModalMessage: '',
      paymentOrderDetails: '',
      paymentEmail: '',

      // SIM selection (for normal mode with imsi_subscription/valability types)
      clientSims: [],
      loadingClientSims: false,
      selectedSimId: null,
      selectedSimData: null,

      // IMSI allocation confirmation dialog
      showImsiAllocationConfirm: false,
      imsiAllocationMessage: '',
      otherClientAllocations: [],
      pendingFormData: null,
    }
  },
  computed: {
    isAdminUser() {
      const user = store.getters.getUser;
      return user?.is_admin || user?.admin || false;
    },
    clientsList() {
      const list = [];
      if (this.currentClient) {
        const statusName = this.currentClient.status_name || 'active';
        list.push({
          id: this.currentClient.id,
          name: this.currentClient.name,
          short_name: this.currentClient.short_name,
          displayName: this.currentClient.name,
          isOwn: true,
          status_name: statusName,
          isActive: statusName === 'active',
          disabled: statusName !== 'active'
        });
      }
      // Recursively add all subclients with hierarchy indentation
      const addSubclientsRecursively = (clients, level = 1) => {
        if (!clients || clients.length === 0) return;
        clients.forEach(sc => {
          const indent = '—'.repeat(level) + ' ';
          const statusName = sc.status_name || 'active';
          const isActive = statusName === 'active';
          list.push({
            id: sc.client_id || sc.id,
            name: sc.name,
            short_name: sc.short_name,
            displayName: indent + sc.name + (isActive ? '' : ` (${statusName})`),
            isOwn: false,
            level: level,
            status_name: statusName,
            isActive: isActive,
            disabled: !isActive
          });
          // Recursively add children
          if (sc.children && sc.children.length > 0) {
            addSubclientsRecursively(sc.children, level + 1);
          }
        });
      };
      addSubclientsRecursively(this.subclients);
      return list;
    },
    filteredServices() {
      // For SIM service addition, use available services from renewData
      if (this.isSimServiceAddition && this.renewData?.availableServices) {
        let services = this.renewData.availableServices;

        // Filter by service type if selected (and not "all")
        if (this.selectedServiceType && this.selectedServiceType !== 'all') {
          services = services.filter(s => s.service_type_name === this.selectedServiceType);
        }

        return services.map(s => {
          const existsOnSim = this.simExistingServiceIds.includes(s.id);
          const existingService = existsOnSim
            ? this.renewData.existingServices?.find(es => es.service_id === s.id)
            : null;
          // Block ALL imsi_subscription services if SIM already has an active subscription
          const blockedBySubscription = s.service_type_name === 'imsi_subscription' && this.simHasActiveSubscription;
          const canAdd = blockedBySubscription ? false : (!existsOnSim || s.allow_multiple === true);
          let displayName = s.name;
          if (blockedBySubscription && !existsOnSim) {
            displayName = `${s.name} (SIM are deja subscriptie activa)`;
          } else if (existsOnSim) {
            displayName = `${s.name} (${s.allow_multiple ? 'permite multiple' : 'exista deja - ' + existingService?.status_name})`;
          }
          return {
            ...s,
            existsOnSim,
            blockedBySubscription,
            existingServiceStatus: existingService?.status_name,
            canAdd,
            name: displayName
          };
        });
      }
      if (!this.selectedServiceType) return [];
      let results = this.services.filter(s => s.service_type_name === this.selectedServiceType);
      // Non-admin users only see services with show_in_search = true
      if (!this.isAdminUser) {
        results = results.filter(s => s.show_in_search);
      }
      return results;
    },
    selectedService() {
      if (!this.selectedServiceId) return null;
      // Check in filteredServices first (for SIM service addition)
      const fromFiltered = this.filteredServices.find(s => s.id === this.selectedServiceId);
      if (fromFiltered) return fromFiltered;
      return this.services.find(s => s.id === this.selectedServiceId);
    },
    requiresSimSelection() {
      return !this.isSimServiceAddition && (this.selectedServiceType === 'imsi_subscription' || this.selectedServiceType === 'valability' || this.selectedServiceType === 'credit');
    },
    requiresMsisdnSelection() {
      return this.selectedServiceType === 'msisdn' || this.selectedServiceType === 'imsi_msisdn';
    },
    parsedImsiSerialList() {
      if (!this.imsiSerialListText || !this.imsiSerialListText.trim()) return [];
      return this.imsiSerialListText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0 && /^\d+$/.test(line));
    },
    canSubmit() {
      // For SIM service addition mode
      if (this.isSimServiceAddition) {
        if (!this.selectedServiceId) return false;
        // Check if service can be added (allow_multiple check)
        const service = this.filteredServices.find(s => s.id === this.selectedServiceId);
        if (service && !service.canAdd) return false;
        return true;
      }
      // Normal mode
      if (!this.selectedClientId) return false;
      if (!this.selectedServiceId) return false;
      if (this.requiresSimSelection && !this.selectedSimData) return false;
      if (this.requiresMsisdnSelection && !this.selectedMsisdnData) return false;
      return true;
    },
    isRenewMode() {
      return this.renewData !== null && !this.renewData.isSimServiceAddition;
    },
    isSimServiceAddition() {
      return this.renewData?.isSimServiceAddition === true;
    },
    simServiceTypes() {
      // Get unique service types from available services for this SIM
      if (!this.isSimServiceAddition || !this.renewData?.availableServices) return [];
      const types = new Map();
      this.renewData.availableServices.forEach(s => {
        if (s.service_type_name && !types.has(s.service_type_name)) {
          types.set(s.service_type_name, { id: s.service_type_id, name: s.service_type_name });
        }
      });
      return Array.from(types.values());
    },
    simExistingServiceIds() {
      // Get IDs of services that already exist on this SIM (active or expired only)
      if (!this.isSimServiceAddition || !this.renewData?.existingServices) return [];
      return this.renewData.existingServices
        .filter(s => s.status_name?.toLowerCase() === 'active' || s.status_name?.toLowerCase() === 'expired')
        .map(s => s.service_id);
    },
    simHasActiveSubscription() {
      // Check if SIM already has a non-terminated imsi_subscription
      if (!this.isSimServiceAddition || !this.renewData?.existingServices) return false;
      return this.renewData.existingServices.some(s =>
        s.service_type_name === 'imsi_subscription' &&
        s.status_name?.toLowerCase() !== 'terminated'
      );
    },
    isTargetClientPrepaid() {
      return this.targetClient?.is_prepaid === true;
    },
    selectedServiceBillingType() {
      return this.selectedService?.billing_type_name || null;
    },
    serviceHasValidity() {
      return this.selectedService?.validity && this.selectedService.validity > 0;
    },
    requiresPayment() {
      // Free services never require payment
      if (this.selectedServiceBillingType === 'free') return false;
      // Prepaid clients always pay upfront (except free)
      if (this.isTargetClientPrepaid) return true;
      // Postpaid clients don't pay upfront
      return false;
    },
    billingAlertType() {
      if (this.selectedServiceBillingType === 'free') return 'success';
      if (this.isTargetClientPrepaid) return 'warning';
      return 'info';
    },
    submitButtonColor() {
      // For IMSI service type: ONLY use IMSI charging option (doNotChargeImsisCost) to determine color
      if (this.selectedServiceType === 'imsi') {
        if (this.doNotChargeImsisCost === true) return 'success';  // Free - green
        if (this.isTargetClientPrepaid) return 'warning';  // Prepaid needs to pay - yellow
        return 'primary';  // Postpaid - blue
      }
      // For other service types: use service billing type
      if (this.selectedServiceBillingType === 'free') return 'success';
      if (this.isTargetClientPrepaid) return 'warning';
      return 'primary';
    },
    submitButtonText() {
      if (this.isRenewMode) return 'Renew Service';
      if (this.isSimServiceAddition) return 'Adauga pe SIM';
      // For IMSI service type: ONLY use IMSI charging option (doNotChargeImsisCost) to determine text
      if (this.selectedServiceType === 'imsi') {
        if (this.doNotChargeImsisCost === true) return 'Add Free Service';
        if (this.isTargetClientPrepaid) return 'Add & Pay';
        return 'Add Service';
      }
      // For other service types: use service billing type
      if (this.selectedServiceBillingType === 'free') return 'Add Free Service';
      if (this.isTargetClientPrepaid) return 'Add & Pay';
      return 'Add Service';
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        this.showDialog = val;
        if (val) {
          this.initDialog();
        }
      }
    },
    startDate(newVal) {
      this.updateFormattedStartDate(newVal);
      this.calculateExpirationDate(newVal);
    }
  },
  methods: {
    async initDialog() {
      this.token = store.getters.getToken;
      this.resetForm();

      // Determine target client: initialClientId prop, or renewData.clientId (from SIM service addition)
      const targetClientId = this.initialClientId
        ? parseInt(this.initialClientId)
        : (this.renewData?.clientId ? parseInt(this.renewData.clientId) : null);
      await this.loadServicesData(targetClientId);

      // If renewing, pre-fill the form with renewData
      if (this.renewData) {
        await this.prefillFromRenewData();
      }
    },
    async prefillFromRenewData() {
      log("Prefilling form with renew data:");
      log(JSON.stringify(this.renewData));

      // Handle SIM service addition mode
      if (this.renewData.isSimServiceAddition) {
        log("SIM Service Addition mode");
        // Set client from SIM data
        if (this.renewData.clientId) {
          this.selectedClientId = this.renewData.clientId;
        }
        // Set service type to 'all' to show all available services
        this.selectedServiceType = 'all';
        // Pre-set MSISDN data from SIM
        if (this.renewData.msisdnId && this.renewData.msisdnNumber) {
          this.selectedMsisdnData = {
            msisdn_id: this.renewData.msisdnId,
            msisdn: this.renewData.msisdnNumber,
            billing_type: 'rent',
            billing_period_id: null,
            billing_period_name: null,
            price: null,
            currency: null
          };
        }
        return;
      }

      // Normal renew mode
      // Set client first
      if (this.renewData.clientId) {
        this.selectedClientId = this.renewData.clientId;
        // Reload services for this client and wait for it to complete
        await this.loadServicesData(this.renewData.clientId);
      }

      // Wait for Vue to update the DOM
      await this.$nextTick();

      // Set service type
      if (this.renewData.serviceTypeName) {
        this.selectedServiceType = this.renewData.serviceTypeName;
        log("Set service type to: " + this.selectedServiceType);
      }

      // Wait for filtered services to update
      await this.$nextTick();

      // Set service - verify it exists in the filtered list
      if (this.renewData.serviceId) {
        const serviceExists = this.filteredServices.find(s => s.id === this.renewData.serviceId);
        if (serviceExists) {
          this.selectedServiceId = this.renewData.serviceId;
          log("Set service ID to: " + this.selectedServiceId);
        } else {
          log("Warning: Service ID " + this.renewData.serviceId + " not found in filtered services");
          log("Available services: " + JSON.stringify(this.filteredServices.map(s => ({id: s.id, name: s.name}))));
        }
      }

      // Set description
      if (this.renewData.description) {
        this.description = this.renewData.description;
      }

      // For msisdn/imsi_msisdn types, pre-set the MSISDN data
      if (this.renewData.msisdnId && this.renewData.msisdnNumber) {
        this.selectedMsisdnData = {
          msisdn_id: this.renewData.msisdnId,
          msisdn: this.renewData.msisdnNumber,
          billing_type: 'renew',
          billing_period_id: null,
          billing_period_name: 'Renew',
          price: null, // Will be determined by API
          currency: null
        };
      }
    },
    resetForm() {
      this.selectedClientId = null;
      this.selectedServiceType = null;
      this.selectedServiceId = null;
      this.selectedMsisdnData = null;
      this.clientSims = [];
      this.selectedSimId = null;
      this.selectedSimData = null;
      this.description = '';
      this.autorenew = false;
      this.startDate = new Date();
      this.formattedStartDate = this.formatDate(new Date());
      this.calculateExpirationDate(new Date());
      this.imsiSerialListText = '';
      this.imsiSerialListErrors = [];
      this.doNotChargeImsisCost = false;
      this.chargeStandardPrice = true;
      this.chargeCostOfSims = false;
      this.pricePerSim = 0;
      this.fixedPrice = 0;
      this.setDeliveryCosts = false;
      this.selectedDeliveryTypeId = null;
      this.setSpecialDeliveryCost = false;
      this.specialDeliveryCost = 0;
      this.selectedImsiTypeId = null;
    },
    validateImsiSerialList() {
      this.imsiSerialListErrors = [];
      if (!this.imsiSerialListText || !this.imsiSerialListText.trim()) return;

      const lines = this.imsiSerialListText.split('\n');
      const invalidLines = [];

      lines.forEach((line, index) => {
        const trimmed = line.trim();
        if (trimmed.length > 0 && !/^\d+$/.test(trimmed)) {
          invalidLines.push(`Line ${index + 1}: "${trimmed}" contains invalid characters`);
        }
      });

      if (invalidLines.length > 0) {
        this.imsiSerialListErrors = invalidLines.slice(0, 3); // Show max 3 errors
        if (invalidLines.length > 3) {
          this.imsiSerialListErrors.push(`... and ${invalidLines.length - 3} more errors`);
        }
      }
    },
    async loadServicesData(targetClientId = null) {
      this.loadingServices = true;
      try {
        const options = {
          include_subclients: true
        };

        // If target client specified, filter services by that client's type
        if (targetClientId) {
          options.target_client_id = targetClientId;
        }

        const response = await getServicesForAddClientService(this.token, options);

        if (response.error === false) {
          this.services = response.services || [];
          this.serviceTypes = response.service_types || [];
          this.deliveryTypes = response.delivery_types || [];
          this.imsiTypes = response.imsi_types || [];
          this.subclients = response.subclients || [];
          this.currentClient = response.current_client || null;
          this.targetClient = response.target_client || null;

          // Auto-select client: use targetClientId, initialClientId prop, or current client
          if (!this.selectedClientId) {
            if (targetClientId) {
              // If targetClientId was passed (e.g., from initialClientId), use it
              this.selectedClientId = targetClientId;
              log("Pre-selected client from targetClientId: " + this.selectedClientId);
            } else if (this.initialClientId) {
              // Find the client in the list and select it
              const clientExists = this.clientsList.find(c => c.id == this.initialClientId);
              if (clientExists) {
                this.selectedClientId = parseInt(this.initialClientId);
                log("Pre-selected client from initialClientId: " + this.selectedClientId);
              } else if (this.currentClient) {
                this.selectedClientId = this.currentClient.id;
              }
            } else if (this.currentClient) {
              this.selectedClientId = this.currentClient.id;
            }
          }

          log("Loaded " + this.services.length + " services for client " + (targetClientId || 'default'));
          log("Target client billing type: " + (this.targetClient?.billing_type_name || 'unknown'));
        } else {
          log("Error loading services: " + response.message);
        }
      } catch (err) {
        log("Exception loading services:");
        log(err);
      } finally {
        this.loadingServices = false;
      }
    },
    async onClientChange(clientId) {
      log("Client changed to: " + clientId);
      this.clientSelectionError = '';

      // Check if selected client is active
      if (clientId) {
        const selectedClient = this.clientsList.find(c => c.id === clientId);
        if (selectedClient && !selectedClient.isActive) {
          this.clientSelectionError = `Client "${selectedClient.name}" is ${selectedClient.status_name}. Only active clients can receive new services.`;
          this.selectedClientId = null;
          return;
        }
      }

      // Reset service selection when client changes
      this.selectedServiceId = null;
      this.selectedServiceType = null;
      this.selectedMsisdnData = null;
      this.clientSims = [];
      this.selectedSimId = null;
      this.selectedSimData = null;

      // Reload services filtered by the selected client's type
      if (clientId) {
        await this.loadServicesData(clientId);
      }
    },
    showInactiveClientError(client) {
      this.clientSelectionError = `Client "${client.name}" is ${client.status_name}. Only active clients can receive new services.`;
    },
    onClientFieldFocus() {
      // Clear search text on focus to allow immediate typing
      this.clientSelectionError = '';
      this.$nextTick(() => {
        this.clientSearchText = '';
      });
    },
    onServiceTypeChange(type) {
      log("Service type changed to: " + type);
      // Reset service selection when type changes
      this.selectedServiceId = null;
      this.selectedMsisdnData = null;
      // Reset SIM selection
      this.selectedSimId = null;
      this.selectedSimData = null;
      this.clientSims = [];
      // Reset IMSI list, charge options, and delivery options
      this.imsiSerialListText = '';
      this.imsiSerialListErrors = [];
      this.doNotChargeImsisCost = false;
      this.chargeStandardPrice = true;
      this.chargeCostOfSims = false;
      this.pricePerSim = 0;
      this.fixedPrice = 0;
      this.setDeliveryCosts = false;
      this.selectedDeliveryTypeId = null;
      this.setSpecialDeliveryCost = false;
      this.specialDeliveryCost = 0;
      // Reset IMSI type selection
      this.selectedImsiTypeId = null;
      // Load client SIMs if the new type requires SIM selection
      if (this.requiresSimSelection && this.selectedClientId) {
        this.loadClientSims(this.selectedClientId);
      }
    },
    onServiceChange(serviceId) {
      log("Service changed to: " + serviceId);
      // Reset MSISDN selection when service changes
      this.selectedMsisdnData = null;
      // Recalculate expiration date based on new service's validity
      this.$nextTick(() => {
        this.calculateExpirationDate(this.startDate);
      });
    },
    onDoNotChargeImsisCostChange(val) {
      if (val) {
        // If "Nu taxa" is ON, disable the other two
        this.chargeStandardPrice = false;
        this.chargeCostOfSims = false;
      } else {
        // If turning off, default to standard price
        this.chargeStandardPrice = true;
      }
    },
    onChargeStandardPriceChange(val) {
      if (val) {
        // If standard price is ON, turn off custom cost
        this.chargeCostOfSims = false;
      }
    },
    onChargeCostOfSimsChange(val) {
      if (val) {
        // If custom cost is ON, turn off standard price
        this.chargeStandardPrice = false;
      }
    },
    async loadClientSims(clientId) {
      if (!clientId) return;
      this.loadingClientSims = true;
      try {
        const response = await getSimsList(this.token, {
          client_id: clientId,
          sim_status_id: 'all_except_terminated',
          per_page: 100
        });
        if (response.error) {
          log("Error loading client SIMs: " + response.message);
          this.clientSims = [];
          return;
        }
        const sims = response.sims || [];
        this.clientSims = sims.map(sim => {
          const hasActiveSubscription = sim.client_service_status === 'active' &&
            (sim.service_type_name === 'imsi_subscription' || sim.service_name?.toLowerCase().includes('subscription'));
          const label = `Serial: ${sim.serial || '-'} | IMSI: ${sim.imsi || '-'} | MSISDN: ${sim.msisdn || '-'}`;
          return {
            id: sim.id,
            imsi_id: sim.imsi_id,
            msisdn_id: sim.msisdn_id,
            serial: sim.serial,
            imsi: sim.imsi,
            msisdn: sim.msisdn,
            has_active_subscription: hasActiveSubscription,
            client_service_status: sim.client_service_status,
            service_name: sim.service_name,
            label: label
          };
        });
        log("Loaded " + this.clientSims.length + " SIMs for client " + clientId);
      } catch (err) {
        log("Exception loading client SIMs:");
        log(err);
        this.clientSims = [];
      } finally {
        this.loadingClientSims = false;
      }
    },
    isSimDisabledForType(sim) {
      if (this.selectedServiceType === 'imsi_subscription') {
        // For imsi_subscription: disable SIMs that already have active subscription
        return sim.has_active_subscription;
      }
      if (this.selectedServiceType === 'valability') {
        // For valability: disable SIMs that DON'T have active subscription
        return !sim.has_active_subscription;
      }
      return false;
    },
    onSimSelected(simId) {
      if (!simId) {
        this.selectedSimData = null;
        return;
      }
      const sim = this.clientSims.find(s => s.id === simId);
      if (sim) {
        this.selectedSimData = sim;
        log("Selected SIM: " + JSON.stringify(sim));
      }
    },
    formatServiceTypeName(name) {
      const names = {
        'client': 'Client',
        'imsi': 'IMSI',
        'msisdn': 'MSISDN',
        'imsi_msisdn': 'IMSI + MSISDN',
        'imsi_subscription': 'IMSI Subscription',
        'valability': 'Valability'
      };
      return names[name] || name;
    },
    openMsisdnSelector() {
      this.showDialog = false;
      this.showMsisdnSelector = true;
    },
    closeMsisdnSelector() {
      this.showMsisdnSelector = false;
      this.showDialog = true;
    },
    onMsisdnSelected(data) {
      log("MSISDN selected:");
      log(JSON.stringify(data));
      this.selectedMsisdnData = data;
      this.showMsisdnSelector = false;
      this.showDialog = true;
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    updateFormattedStartDate(date) {
      this.formattedStartDate = this.formatDate(date);
      this.startDateMenu = false;
    },
    calculateExpirationDate(startDate) {
      if (!startDate) {
        this.expirationDate = '';
        return;
      }
      const expDate = new Date(startDate);
      // Use service validity if available, otherwise default to 1 year
      if (this.selectedService?.validity && this.selectedService.validity > 0) {
        expDate.setDate(expDate.getDate() + this.selectedService.validity);
      } else {
        expDate.setFullYear(expDate.getFullYear() + 1);
      }
      this.expirationDate = this.formatDate(expDate);
    },
    handleDialogClose(value) {
      if (!value) {
        this.$emit('close');
      }
    },
    handleEscape() {
      this.$emit('close');
    },
    closeDialog() {
      this.showDialog = false;
      this.$emit('close');
    },
    closePaymentModal() {
      this.showPaymentModal = false;
      this.paymentLink = '';
      this.paymentModalMessage = '';
      this.paymentOrderDetails = '';
      this.paymentEmail = '';
      this.$emit('serviceAdded', { simId: this.selectedSimData?.id || this.renewData?.simId || null });
      this.$emit('close');
    },
    onPaymentLinkClick() {
      // User clicked the payment link - close modal after a short delay
      setTimeout(() => {
        this.closePaymentModal();
      }, 500);
    },
    async submitNewClientService() {
      if (!this.canSubmit) return;

      this.submitting = true;
      try {
        // Use client ID from renewData for SIM service addition, otherwise from selectedClientId
        const clientId = this.isSimServiceAddition ? this.renewData.clientId : this.selectedClientId;

        const form = {
          newClientServiceClientID: clientId,
          newClientServiceServiceID: this.selectedServiceId,
          newClientServiceActivationDate: this.formattedStartDate,
          newClientServiceExpirationDate: this.expirationDate,
          description: this.description,
          autorenew: this.autorenew,
          newClientServiceSendClientNotifications: false,
          newClientServiceSendProviderNotifications: true,
          newClientServicePayToClientID: this.selectedService?.managed_by_client_id || null,
        };

        // For SIM service addition, add IMSI ID
        if (this.isSimServiceAddition && this.renewData.imsiId) {
          form.imsi_id = this.renewData.imsiId;
          form.is_sim_service_addition = true;
          log("Adding service to existing SIM with IMSI ID: " + this.renewData.imsiId);
        }

        // For normal mode with SIM selection (imsi_subscription/valability)
        if (this.requiresSimSelection && this.selectedSimData) {
          form.imsi_id = this.selectedSimData.imsi_id;
          form.is_sim_service_addition = true;
          if (this.selectedSimData.msisdn_id) {
            form.msisdn_id = this.selectedSimData.msisdn_id;
          }
          log("Adding service to SIM from normal mode, IMSI ID: " + this.selectedSimData.imsi_id);
        }

        // Add MSISDN data if applicable
        if (this.requiresMsisdnSelection && this.selectedMsisdnData) {
          form.newClientServiceNumberOfSims = 1;
          form.newClientServiceListaMsisdn = [{
            msisdn_id: this.selectedMsisdnData.msisdn_id,
            msisdn: this.selectedMsisdnData.msisdn,
            billing_type: this.selectedMsisdnData.billing_type,
            billing_period_id: this.selectedMsisdnData.billing_period_id,
            price: this.selectedMsisdnData.price,
            currency: this.selectedMsisdnData.currency
          }];
        } else if (this.isSimServiceAddition && this.renewData.msisdnId) {
          // For SIM service addition without MSISDN selection, use the existing MSISDN
          form.newClientServiceNumberOfSims = 1;
          form.newClientServiceListaMsisdn = [{
            msisdn_id: this.renewData.msisdnId,
            msisdn: this.renewData.msisdnNumber,
            billing_type: 'existing', // Use existing MSISDN
            billing_period_id: null,
            price: null,
            currency: null
          }];
        }

        // Add IMSI type if selected (for imsi_msisdn service type)
        if (this.selectedServiceType === 'imsi_msisdn' && this.selectedImsiTypeId) {
          form.imsi_type_id = this.selectedImsiTypeId;
        }

        // Add IMSI/Serial list if applicable (for imsi service type)
        if (this.selectedServiceType === 'imsi' && this.parsedImsiSerialList.length > 0) {
          form.imsis_serial_list = this.parsedImsiSerialList;
        }

        // Add charge cost of sims options (for imsi service type)
        if (this.selectedServiceType === 'imsi') {
          form.do_not_charge_imsis_cost = this.doNotChargeImsisCost;
          form.charge_standard_price = this.chargeStandardPrice;
          form.charge_cost_of_sims = this.chargeCostOfSims;
          if (this.chargeCostOfSims) {
            form.price_per_sim = this.pricePerSim || 0;
            form.fixed_price = this.fixedPrice || 0;
          }
        }

        // Add delivery type (for imsi service type)
        if (this.selectedServiceType === 'imsi') {
          form.set_delivery_costs = this.setDeliveryCosts;
          if (this.setDeliveryCosts && this.selectedDeliveryTypeId) {
            form.delivery_type_id = this.selectedDeliveryTypeId;
          }
          // Add special delivery cost
          form.set_special_delivery_cost = this.setSpecialDeliveryCost;
          if (this.setSpecialDeliveryCost) {
            form.special_delivery_cost = this.specialDeliveryCost || 0;
          }
        }

        log("Submitting form:");
        log(JSON.stringify(form));

        const response = await addNewClientService(this.token, form);

        if (response.error === true) {
          // Check for specific error types
          if (response.error_type === 'same_client_allocation') {
            // IMSI already allocated to the same client - show error
            alert(response.message || "IMSI-urile sunt deja alocate acestui client");
          } else if (response.error_type === 'imsi_msisdn_allocation') {
            // IMSI is part of IMSI+MSISDN service - cannot auto-terminate, show detailed error
            alert("Nu se poate aloca IMSI-ul!\n\n" + (response.message || "IMSI-urile sunt alocate in servicii de tip IMSI+MSISDN si trebuie terminate manual."));
          } else {
            alert(response.message || "Error adding service");
          }
        } else if (response.requires_confirmation) {
          // IMSI allocated to other clients - ask for confirmation to terminate
          this.otherClientAllocations = response.other_client_allocations || [];
          this.imsiAllocationMessage = response.message;
          this.pendingFormData = form;
          this.showImsiAllocationConfirm = true;
        } else {
          // Check if payment is required (has btUrl from BT - Banca Transilvania)
          if (response.btUrl) {
            // Show payment modal
            this.paymentLink = response.btUrl;
            this.paymentEmail = response.email || '';
            this.paymentModalMessage = `Comanda va fi activată după efectuarea plății.${this.paymentEmail ? ` Vei fi informat prin email la '${this.paymentEmail}' despre statusul comenzii.` : ''}`;
            this.paymentOrderDetails = `Ai plasat o comandă pentru serviciul ${this.selectedService?.name || ''}`;
            this.showDialog = false;
            this.showPaymentModal = true;
          } else {
            // No payment required - just show success
            log ("Service added successfully!");
            this.$emit('serviceAdded', { simId: this.selectedSimData?.id || this.renewData?.simId || null });
            this.closeDialog();
          }
        }
      } catch (err) {
        log("Error submitting:");
        log(err);
        alert("Error adding service: " + err.message);
      } finally {
        this.submitting = false;
      }
    },

    cancelImsiAllocationConfirm() {
      this.showImsiAllocationConfirm = false;
      this.otherClientAllocations = [];
      this.imsiAllocationMessage = '';
      this.pendingFormData = null;
    },

    async confirmTerminateAndAdd() {
      if (!this.pendingFormData) return;

      this.showImsiAllocationConfirm = false;
      this.submitting = true;

      try {
        // Add force_terminate_existing flag to the form
        const form = { ...this.pendingFormData, force_terminate_existing: true };

        log("Submitting with force terminate:");
        log(JSON.stringify(form));

        const response = await addNewClientService(this.token, form);

        if (response.error === true) {
          alert(response.message || "Error adding service");
        } else {
          // Check if payment is required (has btUrl from BT - Banca Transilvania)
          if (response.btUrl) {
            // Show payment modal
            this.paymentLink = response.btUrl;
            this.paymentEmail = response.email || '';
            this.paymentModalMessage = `Comanda va fi activată după efectuarea plății.${this.paymentEmail ? ` Vei fi informat prin email la '${this.paymentEmail}' despre statusul comenzii.` : ''}`;
            this.paymentOrderDetails = `Ai plasat o comandă pentru serviciul ${this.selectedService?.name || ''}`;
            this.showDialog = false;
            this.showPaymentModal = true;
          } else {
            // No payment required - just show success
            alert("Service added successfully! Serviciile existente au fost terminate.");
            this.$emit('serviceAdded', { simId: this.selectedSimData?.id || this.renewData?.simId || null });
            this.closeDialog();
          }
        }
      } catch (err) {
        log("Error submitting with force terminate:");
        log(err);
        alert("Error adding service: " + err.message);
      } finally {
        this.submitting = false;
        this.pendingFormData = null;
        this.otherClientAllocations = [];
        this.imsiAllocationMessage = '';
      }
    }
  }
});
</script>

<style scoped>
.text-subtitle-1 {
  color: #1976d2;
}
</style>
